import { useCallback, useEffect } from 'react';
import { ui } from '@procuros/datachecks';

import { useNavigationContext } from 'contexts/SchemableFormNavigationContext/SchemableFormNavigationContext';
import { isWebEDIFormError, WebEDIAnnotation } from 'services/webediErrors/types';
import { useWebEDIStore } from 'stores/webedi/webedi';
import { FieldState } from 'support/types/ui';
import { useInterpolatedEnumerableOptions } from 'components/SchemableForm/hooks/useInterpolatedEnumerableOptions';
import { Translate } from 'components/Translate/Translate';

import { CheckboxGroup } from '../CheckboxGroup/CheckboxGroup';

type SchemableFormCheckboxGroupProps = {
  field: ui.EnumerableField;
  value: Array<string>;
  onChange: (value: Array<string>) => void;
  onBlur: () => void;
  error: WebEDIAnnotation | undefined;
  onShowFieldErrorModal?: (showFieldErrorModal: boolean) => void;
  required: boolean;
  disabled: boolean;
  fieldState: FieldState;
  variant?: 'large';
  name: string;
};

export const SchemableFormCheckboxGroup = ({
  field,
  value,
  onChange,
  onBlur,
  error,
  onShowFieldErrorModal,
  required,
  disabled,
  fieldState,
  variant,
  name,
}: SchemableFormCheckboxGroupProps) => {
  const { options, hasResolved } = useInterpolatedEnumerableOptions(field.type_config);
  const isValidating = useWebEDIStore((state) => state.isValidating);
  const { NavigableItemPopoverComponent, useNavigableItemPopover } = useNavigationContext();
  const { showNavigableItemPopover, setShowNavigableItemPopover, floatingStyles, refs } = useNavigableItemPopover();
  const isLargeVariant = variant === 'large';

  const onCheckboxFocus = useCallback(() => {
    setShowNavigableItemPopover(true);
  }, [setShowNavigableItemPopover]);

  const onCheckboxBlur = useCallback(() => {
    onBlur();
    setShowNavigableItemPopover(false);
  }, [onBlur, setShowNavigableItemPopover]);

  const handleToggle = useCallback(
    (optionValue: string) => {
      const currentValues = value ?? [];
      const newValues = currentValues.includes(optionValue)
        ? currentValues.filter((v) => v !== optionValue)
        : [...currentValues, optionValue];
      onChange(newValues);
    },
    [value, onChange],
  );

  useEffect(() => {
    if (error && showNavigableItemPopover) {
      onShowFieldErrorModal?.(true);
    }
  }, [error, showNavigableItemPopover, onShowFieldErrorModal]);

  useEffect(() => {
    if (!hasResolved) {
      return;
    }

    const validValues = new Set(options.map((option) => option.value));
    const currentValues = value ?? [];
    const filteredValues = currentValues.filter((v) => validValues.has(v));

    if (filteredValues.length !== currentValues.length) {
      onChange(filteredValues);
    }
  }, [options, hasResolved, value, onChange]);

  const inlineErrors =
    isLargeVariant && isWebEDIFormError(error) ? { message: error.description, type: error.type } : undefined;

  return (
    <div ref={isLargeVariant ? undefined : refs.setReference} data-field={name} tabIndex={-1}>
      <CheckboxGroup.Group
        label={field.label}
        legendVariant={isLargeVariant ? 'large' : 'default'}
        required={required}
        description={field.description ? <Translate i18nKey={field.description} /> : undefined}
      >
        {options.map((option) => (
          <CheckboxGroup.Item
            key={option.value}
            name={name}
            value={option.value}
            label={option.label}
            description={option.description || undefined}
            checked={(value ?? []).includes(option.value)}
            onChange={() => handleToggle(option.value)}
            onFocus={isLargeVariant ? undefined : onCheckboxFocus}
            onBlur={isLargeVariant ? undefined : onCheckboxBlur}
            disabled={disabled}
            hasErrors={fieldState === 'error'}
          />
        ))}
      </CheckboxGroup.Group>

      {inlineErrors && <p className="mt-1 text-sm text-red-700">{inlineErrors.message}</p>}

      {!isLargeVariant && showNavigableItemPopover && NavigableItemPopoverComponent && (
        <NavigableItemPopoverComponent
          viewMode={false}
          annotation={error}
          ref={refs.setFloating}
          style={floatingStyles}
          isValidating={isValidating}
          isPendingValidation={fieldState === 'pending-validation'}
          fieldDescription={field.description}
        />
      )}
    </div>
  );
};

SchemableFormCheckboxGroup.displayName = 'SchemableFormCheckboxGroup';
